/**
 * ⚠️ Status: Current System - Migration in Progress
 *
 * This is the current color system in use, but it will eventually be replaced by colors.ts.
 *
 * For new development:
 * - Consider using colors.ts for new components
 * - Test thoroughly if mixing with new system and please reach out to design for intended usage
 * - Expect future migration to colors.ts
 *
 * Recent changes:
 * - Removed unused colors
 * - Added comprehensive documentation
 * - Future migration to colors.ts, typography.ts, spacing.ts planned
 */

/**
 * Color system for the P0 application.
 *
 * NOTE: This file will eventually merge with colors.ts, which represents the future color system setup.
 * For new development, prefer using colors.ts as it provides a more comprehensive and theme-aware color system.
 *
 * Usage guidelines:
 * - Primary: Main actions, important buttons, brand elements
 *   Example: Primary buttons, important CTAs, brand highlights
 *
 * - Neutral: Text, backgrounds, borders, UI elements
 *   Example: Body text (70), Subtle text (40), Backgrounds (00-20)
 *
 * - Success: Success states, positive feedback, completed actions
 *   Example: Success messages, completed states, positive indicators
 *
 * - Tag: Status indicators, labels, tags
 *   Example: Status tags, severity indicators, labels
 *
 * - Theme: Specific UI elements and data visualization
 *   Example: Charts, graphs, specific UI components
 *
 * - Graph: Charts and data visualization only
 *   Example: Line charts, bar graphs, data visualizations
 *
 * For detailed documentation and examples, see COLORS.md
 */
export const colors = {
  // Primary colors for main actions and brand identity
  // Usage: Buttons, CTAs, brand elements
  // Example: <Button style={{ backgroundColor: colors.primary["50"] }}>
  primary: {
    "00": "#e6f7ff", // Lightest - Used for hover states
    "40": "#40a9ff", // Light - Used for secondary actions
    "50": "#1890ff", // Main - Used for primary actions
    "60": "#096dd9", // Dark - Used for active states
    "100": "#001a33", // Darkest - Used for text on light backgrounds
  },
  // Neutral colors for text, backgrounds, and UI elements
  // Usage: Text, backgrounds, borders, UI elements
  // Example: <p style={{ color: colors.neutral["70"] }}>
  neutral: {
    "00": "#ffffff", // White - Backgrounds, cards
    "05": "#fafafa", // Very light - Subtle backgrounds
    "10": "#f5f5f5", // Light - Secondary backgrounds
    "15": "#f0f0f0", // Light - Borders, dividers
    "20": "#d9d9d9", // Light - Borders, disabled states
    "30": "#bfbfbf", // Medium - Secondary text
    "40": "#8c8c8c", // Medium - Placeholder text
    "45": "rgba(0, 0, 0, 0.45)", // Medium - Secondary text
    "50": "#595959", // Medium - Secondary text
    "60": "#434343", // Dark - Body text
    "70": "#262626", // Dark - Primary text
    "80": "#1f1f1f", // Darker - Headings
    "90": "#141414", // Darker - Important text
    "100": "#000000", // Black - Critical text
  },
  // Success colors for positive states and feedback
  // Usage: Success messages, completed states
  // Example: <Tag color={colors.success["50"]}>
  success: {
    "00": "#f6ffed", // Lightest - Success backgrounds
    "50": "#52c41a", // Main - Success indicators
    "60": "#389e0d", // Dark - Success text
    "70": "#237804", // Darkest - Success emphasis
  },
  // Tag colors based on Ant Design's tag colors
  // Usage: Status indicators, severity levels
  // Example: <Tag color={colors.tagColors.blue}>
  tagColors: {
    blue: {
      bg: "#e6f4ff", // Background for info tags
      text: "#0958d9", // Text for info tags
    },
    red: {
      text: "#cf1322", // Text for error/critical tags
      bg: "#fff1f0", // Background for error/critical tags
    },
    yellow: {
      text: "#8c5900", // Text for warning tags
      bg: "#fffbe6", // Background for warning tags
    },
    lightGrey: {
      bg: "#fafafa", // Background for neutral tags
      text: "#000000", // Text for neutral tags
    },
    grey: {
      bg: "#cbc6c6", // Background for disabled tags
      text: "#000000", // Text for disabled tags
    },
    orange: {
      text: "#844306", // Text for high severity tags
      bg: "#FFE4AD", // Background for high severity tags
    },
  },
  // Theme colors for specific UI elements and data visualization
  // Usage: Charts, specific UI components
  // Example: <Chart color={colors.themeColors.red}>
  themeColors: {
    red: "#f94144", // Error states, critical data
    darkOrange: "#f3722c", // High severity warnings
    lightOrange: "#f8961e", // Medium severity warnings
    orange: "#f9844a", // Warning states
    yellow: "#f9c74f", // Caution states
    darkBlue: "#577590", // Information states
    purple: "#6a4c93", // Special states
    lightGreen: "#d9f7be", // Light success states
    blueGreen: "#43aa8b", // Success states
    lightYellow: "#ffffb8", // Light warning states
    veryLightBlue: "#d6e4ff", // Light information states
  },
  // Colors specifically for data visualization and charts
  // Usage: Charts, graphs, data visualizations
  // Example: <Chart colors={[colors.graphColors.purple, colors.graphColors.blue]}>
  graphColors: {
    purple: "#6A4C93", // Primary chart color
    blue: "#5DA939", // Secondary chart color
    pink: "#F5A6E6", // Tertiary chart color
    darkGreen: "#3F6634", // Quaternary chart color
    lightGreen: "#EEF5DB", // Quinary chart color
  },
};
